"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { AccountingAccount, AccountingAccountService } from "../services/accounting-account-service";
import { AccountingAccountTable } from "../components/accounting-account-table";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";

interface Props {
  search?: string;
  reload?: number;
  typeFilter?: string | number | null;
  onEdit?: (item: AccountingAccount) => void;
  onView?: (item: AccountingAccount) => void;
  onLoadedCount?: (count: number) => void;
}

export const InactiveAccountingAccountTab: React.FC<Props> = ({ search = "", reload = 0, typeFilter = "", onEdit, onView, onLoadedCount }) => {
  const [rows, setRows] = useState<AccountingAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const { fetch: fetchList } = AccountingAccountService.list();
  const { fetch: update } = AccountingAccountService.update();
  const fetchListRef = useRef(fetchList);
  useEffect(() => { fetchListRef.current = fetchList; }, [fetchList]);
  const onLoadedCountRef = useRef(onLoadedCount);
  useEffect(() => { onLoadedCountRef.current = onLoadedCount; }, [onLoadedCount]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { status: 0, page, limit };
      if (search) params.key_search = search;
      if (typeFilter) params.type = Number(typeFilter);
      const res = await fetchListRef.current(params);
      if (res && isSuccess(res)) {
        const data: any = res.data || {};
        const list: AccountingAccount[] = Array.isArray(data.list) ? data.list : [];
        setRows(list);
        const total = Number(data.total_record ?? data.total ?? list.length ?? 0);
        setTotalRecords(total);
        onLoadedCountRef.current?.(total);
      } else {
        showToast({ message: res?.message ?? "Không thể tải dữ liệu", type: "error" });
        setRows([]);
        setTotalRecords(0);
        onLoadedCountRef.current?.(0);
      }
    } catch (e) {
      showToast({ message: "Lỗi khi tải dữ liệu", type: "error" });
      setRows([]);
      setTotalRecords(0);
      onLoadedCountRef.current?.(0);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, page, limit]);

  useEffect(() => { void loadData(); }, [reload, search, loadData]);
  useEffect(() => { setPage(1); }, [limit]);

  const handleActivate = async (item: AccountingAccount) => {
    const { id, ...rest } = item as any;
    const res = await update(item.id, { ...rest, status: 1 });
    if (res && isSuccess(res)) {
      showToast({ message: "Đã kích hoạt tài khoản", type: "success" });
      void loadData();
    } else {
      showToast({ message: res?.message ?? "Không thể cập nhật trạng thái", type: "error" });
    }
  };

  return (
    <div className="mt-3">
      <AccountingAccountTable
        data={rows}
        loading={loading}
        isInactive={true}
        onEdit={onEdit}
        onView={onView}
        onChangeStatus={handleActivate}
      />

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          {totalRecords > 0
            ? `Hiển thị ${(page - 1) * limit + 1} đến ${(page - 1) * limit + rows.length} của ${totalRecords} hàng`
            : "Không có dữ liệu"}
        </div>
        <div className="flex items-center gap-3">
          <select
            className="border rounded px-2 py-1"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          {(() => {
            const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
            const pages: (number | string)[] = [];
            const add = (p: number | string) => pages.push(p);
            const start = Math.max(1, page - 1);
            const end = Math.min(totalPages, page + 1);
            add(1);
            if (start > 2) add("...");
            for (let p = start; p <= end; p++) if (p !== 1 && p !== totalPages) add(p);
            if (end < totalPages - 1) add("...");
            if (totalPages > 1) add(totalPages);
            return (
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {"<"}
                </button>
                {pages.map((p, idx) =>
                  typeof p === "number" ? (
                    <button
                      key={idx}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 rounded-full ${p === page ? "bg-green-600 text-white" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}
                    >
                      {p}
                    </button>
                  ) : (
                    <span key={idx} className="px-2">{p}</span>
                  )
                )}
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={page >= Math.ceil(totalRecords / limit)}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {">"}
                </button>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};
